/**
 * analysisApi.ts
 * Typed fetch helpers for the stuttgart-analysis-api Cloud Run service.
 *
 * Base URL:    REACT_APP_ANALYSIS_API_URL
 * Auth:        REACT_APP_ANALYSIS_API_KEY  (x-api-key header)
 */

const BASE_URL = (process.env.REACT_APP_ANALYSIS_API_URL ?? '').replace(/\/$/, '');
const API_KEY  = process.env.REACT_APP_ANALYSIS_API_KEY ?? '';

function apiHeaders(): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (API_KEY) h['x-api-key'] = API_KEY;
  return h;
}

async function apiFetch<T>(path: string): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, { headers: apiHeaders() });
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return res.json() as Promise<T>;
}

// ---- Types ----

export type JourneyStatus = 'SCHEDULED' | 'LIVE' | 'COMPLETE' | 'CANCELLED';
export type FilterMode = 'live' | 'recent' | 'today' | 'date';

export type Journey = {
  journey_id:           string;
  route_id:             string;   // e.g. "7624"
  line_id:              string;   // e.g. "S1"
  service_day:          string;   // "YYYY-MM-DD"
  status:               JourneyStatus;
  origin_stop:          string | null;
  destination_stop:     string | null;
  scheduled_departure:  string | null;   // ISO timestamp
  scheduled_arrival:    string | null;
  route_complete:       boolean | null;
  delay_minutes:        number | null;
};

export type Stop = {
  stop_sequence:        number;
  stop_name:            string;
  stop_eva:             string;
  platform:             string | null;
  scheduled_arrival:    string | null;
  scheduled_departure:  string | null;
  actual_arrival:       string | null;
  actual_departure:     string | null;
  delay_ab_seconds:     number | null;
  delay_an_seconds:     number | null;
  status:               string | null;
  status_ab:            string | null;
  status_an:            string | null;
  cancelled:            boolean;
};

export type JourneysResponse = {
  day:           string;
  line:          string | null;
  filter:        string;
  count:         number;
  status_counts: Record<JourneyStatus, number>;
  server_now:    string;
  journeys:      Journey[];
};

export type StopsResponse = {
  journey_id:  string;
  route_id:    string;
  line_id:     string;
  service_day: string;
  status:      JourneyStatus;
  origin:      string | null;
  destination: string | null;
  stop_count:  number;
  server_now:  string;
  stops:       Stop[];
};

export type LinesResponse = {
  day:   string;
  lines: string[];
  count: number;
};

// ---- API calls ----

export function fetchLines(day?: string): Promise<LinesResponse> {
  const q = day ? `?day=${day}` : '';
  return apiFetch<LinesResponse>(`/api/v1/lines${q}`);
}

export function fetchJourneys(params: {
  day?:    string;
  line?:   string;
  filter?: FilterMode;
  limit?:  number;
}): Promise<JourneysResponse> {
  const q = new URLSearchParams();
  if (params.day)    q.set('day',    params.day);
  if (params.line)   q.set('line',   params.line);
  if (params.filter) q.set('filter', params.filter);
  if (params.limit)  q.set('limit',  String(params.limit));
  return apiFetch<JourneysResponse>(`/api/v1/journeys?${q.toString()}`);
}

export function fetchStops(journeyId: string): Promise<StopsResponse> {
  return apiFetch<StopsResponse>(`/api/v1/stops?journey_id=${encodeURIComponent(journeyId)}`);
}

// ---- Helpers ----

export function formatTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', {
      hour:   '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin',
    });
  } catch {
    return '—';
  }
}

export function formatDelay(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '';
  if (seconds === 0) return 'On time';
  const sign = seconds > 0 ? '+' : '';
  const mins = Math.round(seconds / 60);
  return `${sign}${mins}m`;
}

export function statusColour(status: JourneyStatus): string {
  switch (status) {
    case 'LIVE':      return '#4ade80';   // green
    case 'COMPLETE':  return '#94a3b8';   // slate
    case 'SCHEDULED': return '#64748b';   // muted
    case 'CANCELLED': return '#f87171';   // red
    default:          return '#94a3b8';
  }
}
